import { computed, ref, watch, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { useUsersStore } from '@/store/pinia/users';
import { useMeetingStore } from '@/store/pinia/meeting';
import { usePhaseStore } from '@/store/pinia/phase';
import { usePageStore } from '@/store/pinia/page';
import { useSidebarStore } from '@/store/pinia/sidebar';
import { useFeaturesStore } from '@/store/pinia/features';

export function useReady() {
  const usersStore = useUsersStore();
  const meetingStore = useMeetingStore();
  const phaseStore = usePhaseStore();
  const pageStore = usePageStore();
  const sidebarStore = useSidebarStore();
  const featuresStore = useFeaturesStore();

  const finishedFirstPhaseAnimation = ref(false);

  const { allUsers, firstShown: avatarsFirstShown, visibleUserCount } = storeToRefs(usersStore);
  const { initTitleCardShown, titleCardsShown, titleCardAnimating, titleCardShowing, isScheduled } = storeToRefs(meetingStore);
  const { currentPhase, isAnimating: phaseIsAnimating } = storeToRefs(phaseStore);
  const { currentPageName, isMeeting } = storeToRefs(pageStore);
  const { skillBarShowing, isEntering: sidebarPaneEntering } = storeToRefs(sidebarStore);
  const { noAnimations } = storeToRefs(featuresStore);

  const readyTick = ref(false);
  const ignoreCards = ref(false);

  watch(phaseIsAnimating, (nv, ov) => {
    if (nv === 0 && ov > 0) {
      finishedFirstPhaseAnimation.value = true;
    }
  });

  watch(
    visibleUserCount,
    (nv) => {
      if (nv > 1 && !ignoreCards.value) {
        setTimeout(() => {
          ignoreCards.value = true;
        }, 3000);
      }
    },
    { immediate: true },
  );

  watch(currentPageName, () => {
    finishedFirstPhaseAnimation.value = false;
    ignoreCards.value = false;
  });

  const readyForAvatars = computed(() => {
    return (initTitleCardShown.value || ignoreCards.value) && allUsers.value && allUsers.value.length >= 1;
  });

  const finishedFirstAvatars = computed(() => {
    return (initTitleCardShown.value || ignoreCards.value) && avatarsFirstShown.value;
  });

  const afterCard = computed(() => {
    if (currentPhase.value === 'endMeeting') {
      return true;
    }
    return (initTitleCardShown.value || ignoreCards.value) && currentPhase.value !== 'init';
  });

  const afterLobbyCard = computed(() => {
    if (currentPhase.value !== 'lobby') {
      return false;
    }
    if (ignoreCards.value) {
      return true;
    }
    return !titleCardShowing.value && !titleCardAnimating.value;
  });

  const afterMeetingSpaceCard = computed(() => {
    if (currentPhase.value === 'endMeeting' || ignoreCards.value) {
      return true;
    }
    return !titleCardShowing.value && titleCardsShown.value.includes('meetingSpace') && !titleCardAnimating.value;
  });

  const finishedFirstPhase = computed(() => {
    return (initTitleCardShown.value || ignoreCards.value) && avatarsFirstShown.value && finishedFirstPhaseAnimation.value;
  });

  const isScheduledToFreeformTransition = computed(() => {
    if (!isMeeting.value || !isScheduled.value || currentPhase.value === 'lobby') {
      return false;
    }

    return !afterMeetingSpaceCard.value;
  });

  const skillBarReady = computed(() => readyTick.value && (!sidebarPaneEntering.value || noAnimations.value) && skillBarShowing.value);

  watch(afterMeetingSpaceCard, () => {
    nextTick(() => {
      readyTick.value = true;
    });
  });

  return {
    readyForAvatars,
    finishedFirstAvatars,
    afterCard,
    afterMeetingSpaceCard,
    afterLobbyCard,
    finishedFirstPhase,
    isScheduledToFreeformTransition,
    skillBarReady,
  };
}
